import React, { useState } from 'react';
import { Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react';
import apiFetch from './api';

const GamingLoginPage = ({ onLogin }) => {
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            const response = await apiFetch('/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: credentials.username,
                    password: credentials.password
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Login failed');
            }

            // Store authentication state
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem('username', credentials.username);
            localStorage.setItem('userId', data.user.id);
            
            // Call the onLogin callback
            onLogin(data.user);
            
        } catch (error) {
            setError(error.message || 'Login failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-gray-800">
            {/* Decorative elements */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500 opacity-10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 opacity-10 rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
            </div>

            <div className="relative w-96 p-8 rounded-2xl bg-gray-900 border border-gray-800 shadow-2xl">
                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-500 bg-clip-text ">
                        SM-CMS
                    </h1>
                    <p className="text-gray-400 mt-2">Admin Portal</p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Error Message */}
                    {error && (
                        <div className="bg-red-500 bg-opacity-10 border border-red-500 rounded-lg p-3 flex items-center gap-2">
                            <AlertCircle className="text-red-500" size={20} />
                            <p className="text-red-500 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Username Field */}
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                            Username
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-3 text-gray-500" size={20} />
                            <input
                                type="text"
                                required
                                className="w-full bg-gray-800 text-gray-100 pl-10 pr-4 py-2 rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500 focus:ring-opacity-20 focus:outline-none transition-colors"
                                placeholder="Enter your username"
                                value={credentials.username}
                                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* Password Field */}
                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                            Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gray-500" size={20} />
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                className="w-full bg-gray-800 text-gray-100 pl-10 pr-12 py-2 rounded-lg border border-gray-700 focus:border-amber-500 focus:ring-2 focus:ring-amber-500 focus:ring-opacity-20 focus:outline-none transition-colors"
                                placeholder="Enter your password"
                                value={credentials.password}
                                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-3 text-gray-500 hover:text-gray-300 transition-colors"
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 px-4 rounded-lg bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-gray-900 font-bold shadow-lg transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <div className="flex items-center justify-center">
                                <div className="w-6 h-6 border-4 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        ) : (
                            'Login to Portal'
                        )}
                    </button>
                </form>

                {/* Decorative Border */}
                <div className="absolute inset-px bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 rounded-2xl -z-10 opacity-20"></div>
            </div>
        </div>
    );
};

export default GamingLoginPage;